import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { EMAIL_TEMPLATE_TYPES } from 'constants/email-config';
import { useTranslation } from 'react-i18next';
import { StepSelector } from 'views/NewCheck/components/StepSelector/StepSelector';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { ChecksSelector } from 'components/ChecksSelector/ChecksSelector';
import { EmailTemplateStep } from 'components/EmailTemplateStep/EmailTemplateStep';
import { Button } from 'components/FormComponents';

const STEPS = { CHECKS: 'checks', EMAIL_TEMPLATE: 'email_template' };

// Stable reference so EmailTemplateStep's fetch effect doesn't re-trigger on every render.
const NEW_CHECKS_ADDED_EMAIL_TYPE = [EMAIL_TEMPLATE_TYPES.NEW_CHECKS_ADDED];

export const AddCheckModal = ({
  brand,
  companyChecks,
  company,
  existingChecks = [],
  forms,
  visible,
  className,
  candidate = null,
  allowUploads = false,
  brands = [],
  useDocuments = false,
  getBrandUploads = () => {},
  onSubmit = () => {},
  onClose = () => {},
  getUpload = () => {},
  signUpload = () => {},
  createUpload = () => {},
  deleteUpload = () => {},
}) => {
  const { t } = useTranslation();
  const selectorRef = useRef(null);
  const selectedTemplateIdRef = useRef(null);

  const [valid, setValidity] = useState(false);
  const [step, setStep] = useState(STEPS.CHECKS);
  const [checksSelectorKey, setChecksSelectorKey] = useState(0);

  const resetModalState = useCallback(() => {
    setStep(STEPS.CHECKS);
    setValidity(false);
    selectedTemplateIdRef.current = null;
  }, []);

  useEffect(() => {
    resetModalState();
    if (visible) {
      setChecksSelectorKey(k => k + 1);
    }
  }, [visible, resetModalState]);

  const handleClose = () => {
    onClose();
  };

  const goToEmailStep = async () => {
    const ok = await selectorRef.current?.validate?.();
    if (ok) {
      setStep(STEPS.EMAIL_TEMPLATE);
    }
  };
  const goBack = () => setStep(STEPS.CHECKS);

  const handleChecksSubmit = data => {
    onSubmit({ ...data, template_id: selectedTemplateIdRef.current });
    handleClose();
  };

  const handleEmailStepSubmit = templateId => {
    selectedTemplateIdRef.current = templateId;
    selectorRef.current?.submit?.();
  };

  const getBrandCandidateEmailTemplateId = useCallback(() => {
    return brand?.email_active ? (brand?.email_template_links?.new_checks_added ?? null) : null;
  }, [brand]);

  const isStep2 = step === STEPS.EMAIL_TEMPLATE;

  return (
    <AnimatedModal
      visible={visible}
      onClose={handleClose}
      title="Add New Background Check"
      noPadding>
      <div className={cn({ [className]: className })}>
        <div className="px-6 py-3 bg-neutral-1 border-b border-neutral-3">
          <StepSelector
            activeStep={isStep2 ? 2 : 1}
            invalid={!valid}
            step1Label={t('addCheck.steps.checkDetails')}
            step2Label={t('addCheck.steps.emailTemplate')}
            onStep1Selection={() => isStep2 && goBack()}
            onStep2Selection={() => !isStep2 && valid && goToEmailStep()}
          />
        </div>

        {/* Step 1: Check selection */}
        <div className={cn(isStep2 && 'hidden')}>
          <p className="text-xl text-neutral-6 pt-6 px-6">
            {t('addCheck.selectChecksDescription')}
          </p>
          <ChecksSelector
            key={checksSelectorKey}
            ref={selectorRef}
            company={company}
            companyChecks={companyChecks}
            existingChecks={existingChecks}
            forms={forms}
            brands={brands}
            useDocuments={useDocuments}
            getBrandUploads={getBrandUploads}
            setValidity={setValidity}
            onSubmit={handleChecksSubmit}
            noCandidateUpdate
            candidateData={candidate}
            allowUploads={allowUploads}
            getUpload={getUpload}
            signUpload={signUpload}
            createUpload={createUpload}
            deleteUpload={deleteUpload}
          />
          <div className="p-6">
            <Button onClick={goToEmailStep} disabled={!valid} className="u-width-100">
              {t('addCheck.nextButton')}
            </Button>
          </div>
        </div>

        {/* Step 2: Email template */}
        {isStep2 && (
          <div className="p-6">
            <EmailTemplateStep
              brand={brand}
              category="candidate"
              templateIdInBrandDefault={getBrandCandidateEmailTemplateId()}
              onBack={goBack}
              type={NEW_CHECKS_ADDED_EMAIL_TYPE}
              onSubmit={handleEmailStepSubmit}
            />
          </div>
        )}
      </div>
    </AnimatedModal>
  );
};
